// src/services/navGuard.ts
// -----------------------------------------------------------------------------
// In-app unsaved-changes guard for header navigation. A screen with unsaved
// edits registers a provider that returns a human message while dirty (or null
// when clean); the Header asks confirmNavAway() before switching pages and the
// user chooses Stay / Leave & discard via the standard in-app dialog.
//
// Module-level singleton (same pattern as dialogService): at most ONE provider
// is registered at a time — fine for a single-page app where only the active
// screen can hold unsaved edits. Screens should also add their own
// `beforeunload` listener for browser refresh/close (this guard only covers
// IN-APP navigation; new-tab links and the browser back button bypass it).

import { confirmDialog } from "./dialogService";

type GuardFn = () => string | null;

let current: GuardFn | null = null;

// Register the active screen's guard. Returns an unregister function — call it
// on unmount (a later registration by another screen simply replaces this one).
export function setNavGuard(fn: GuardFn): () => void {
  current = fn;
  return function unregister() {
    if (current === fn) current = null;
  };
}

// True = OK to navigate (no guard, guard reports clean, or the user chose to
// leave). False = the user chose to stay.
export async function confirmNavAway(): Promise<boolean> {
  if (!current) return true;
  const msg = current();
  if (!msg) return true;
  return confirmDialog({
    title: "Unsaved changes",
    message: msg,
    confirmLabel: "Leave & discard",
    cancelLabel: "Stay",
    danger: true,
  });
}
